/**
 * LeaderboardScreen - Premium leaderboard with podium design matching v0
 */

import { useEffect, useState } from 'react';
import type { Referrer } from '../types/agent';
import { getTopReferrers } from '../lib/api';
import { hapticFeedback } from '../lib/telegram';

interface LeaderboardScreenProps {
  onAgentClick: (agentId: string) => void;
}

const MEDALS = ['🥇', '🥈', '🥉'];

export function LeaderboardScreen({ onAgentClick }: LeaderboardScreenProps) {
  const [referrers, setReferrers] = useState<Referrer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      try {
        const res = await getTopReferrers(20);
        setReferrers(res.referrers);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 pb-24 animate-screen-fade">
        <div className="text-center pt-6">
          <div className="w-12 h-12 rounded-full bg-[#242424] animate-pulse mx-auto mb-3" />
          <div className="w-32 h-6 bg-[#242424] rounded animate-pulse mx-auto mb-2" />
          <div className="w-48 h-4 bg-[#242424] rounded animate-pulse mx-auto" />
        </div>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-16 rounded-xl bg-[#242424] animate-pulse mx-6" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto pb-24 animate-screen-fade">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-[#0098EA]/10 flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-[#0098EA]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Leaderboard</h1>
          <p className="text-[#a0a0a0]">Top referrers this month</p>
        </div>

        {referrers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-4xl mb-4">🏆</p>
            <h3 className="text-base font-semibold text-white mb-2">No referrals yet</h3>
            <p className="text-sm text-[#a0a0a0]">
              Be the first to refer an app and claim the #1 spot!
            </p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {referrers.length >= 3 && (
              <div className="flex items-end justify-center gap-2 px-2">
                {/* Second Place */}
                <PodiumPosition referrer={referrers[1]} rank={2} onClick={onAgentClick} />
                {/* First Place */}
                <PodiumPosition referrer={referrers[0]} rank={1} onClick={onAgentClick} />
                {/* Third Place */}
                <PodiumPosition referrer={referrers[2]} rank={3} onClick={onAgentClick} />
              </div>
            )}

            {/* Rest of Leaderboard */}
            <div className="space-y-2">
              {referrers.slice(referrers.length >= 3 ? 3 : 0).map((ref, i) => {
                const rank = (referrers.length >= 3 ? 3 : 0) + i + 1;
                return (
                  <div
                    key={ref.id}
                    onClick={() => { hapticFeedback('light'); onAgentClick(ref.id); }}
                    className="bg-[#242424] border border-white/5 rounded-xl p-4 flex items-center gap-4 hover:border-white/10 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-white">
                        {rank <= 3 ? MEDALS[rank - 1] : `#${rank}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center overflow-hidden flex-shrink-0">
                        {ref.logo ? (
                          <img src={ref.logo} alt={ref.name} className="w-full h-full object-cover" onError={(e) => {
                            const t = e.target as HTMLImageElement; t.style.display = 'none'; t.parentElement!.innerHTML = '<span class="text-xl">🤖</span>';
                          }} />
                        ) : <span className="text-xl">🤖</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{ref.name}</p>
                        <p className="text-xs text-[#a0a0a0]">referrals</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-white">{ref.referral_count}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PodiumPosition({ referrer, rank, onClick }: { referrer: Referrer; rank: number; onClick: (id: string) => void }) {
  const isFirst = rank === 1;
  const bgColors = { 1: 'border-[#FFD700]', 2: 'border-[#C0C0C0]', 3: 'border-[#CD7F32]' };
  const badgeBg = { 1: 'bg-[#FFD700]', 2: 'bg-[#C0C0C0]', 3: 'bg-[#CD7F32]' };
  const heights = { 1: 'h-32', 2: 'h-24', 3: 'h-20' };
  const avatarSize = isFirst ? 'w-[72px] h-[72px]' : 'w-[56px] h-[56px]';

  return (
    <div
      onClick={() => { hapticFeedback('light'); onClick(referrer.id); }}
      className={`flex-1 text-center cursor-pointer active:scale-95 transition-transform ${isFirst ? 'order-2' : rank === 2 ? 'order-1' : 'order-3'}`}
    >
      {/* Avatar */}
      <div className="relative inline-block mb-2">
        <div className={`${avatarSize} rounded-full bg-[#242424] flex items-center justify-center border-2 ${bgColors[rank as 1|2|3]} shadow-lg overflow-hidden`}>
          {referrer.logo ? (
            <img src={referrer.logo} alt={referrer.name} className="w-full h-full object-cover" onError={(e) => {
              const t = e.target as HTMLImageElement; t.style.display = 'none'; t.parentElement!.innerHTML = `<span class="text-${isFirst ? '2xl' : 'lg'} font-bold text-white">🤖</span>`;
            }} />
          ) : (
            <span className={`font-bold text-white ${isFirst ? 'text-xl' : 'text-lg'}`}>🤖</span>
          )}
        </div>
        <div className={`absolute ${isFirst ? '-top-2 -right-2 w-8 h-8' : '-top-1 -right-1 w-6 h-6'} ${badgeBg[rank as 1|2|3]} rounded-full flex items-center justify-center border-2 border-[#1a1a1a] shadow-lg`}>
          <span className={`${isFirst ? 'text-sm' : 'text-xs'} font-bold text-[#1a1a1a]`}>{rank === 1 ? '👑' : rank}</span>
        </div>
      </div>

      {/* Podium bar */}
      <div className={`bg-[#242424] rounded-t-xl border border-white/5 ${heights[rank as 1|2|3]} ${isFirst ? 'p-4 border-t-2 border-t-[#FFD700]' : 'p-3'} flex flex-col justify-center`}>
        <p className={`font-semibold text-white ${isFirst ? 'text-sm' : 'text-[11px]'} truncate mb-1`}>{referrer.name}</p>
        <p className="text-xs text-[#a0a0a0] mb-1">referrals</p>
        <p className={`${isFirst ? 'text-2xl' : 'text-lg'} font-bold text-white`}>{referrer.referral_count}</p>
      </div>
    </div>
  );
}
